import { PortalHost } from "@gorhom/portal";
import React, { createContext, FC, JSX, ReactNode, useContext, useMemo } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";
import Animated from "react-native-reanimated";
import invariant from "tiny-invariant";
import { Layout as UiLayout } from "@lookiero/sty-psp-ui";
import { style as layoutStyle } from "../layout/Layout.style";
import { useHideHeaderOnScroll } from "../layout/useHideHeaderOnScroll";
import { style } from "./OfferingLayout.style";

const OFFERING_FOOTER_PORTAL_HOST = "LookInOfferingFooter";

type OnScrollFunction = (event: NativeSyntheticEvent<NativeScrollEvent>) => void;

interface OfferingLayoutContextApi {
  readonly onScroll: OnScrollFunction;
}

const OfferingLayoutContext = createContext<OfferingLayoutContextApi | null>(null);

const useOfferingLayout = (): OfferingLayoutContextApi => {
  const context = useContext(OfferingLayoutContext);

  invariant(
    context,
    "You are trying to use the useOfferingLayout hook without wrapping your app with the <OfferingLayout>.",
  );

  return context;
};

interface OfferingLayoutProps {
  readonly children: ReactNode;
  readonly header: JSX.Element;
  readonly layout: UiLayout;
}

const OfferingLayout: FC<OfferingLayoutProps> = ({ children, header, layout: Layout }) => {
  const { headerAnimatedStyle, scrollViewAnimatedStyle, handleOnScroll } = useHideHeaderOnScroll();

  const value = useMemo(() => ({ onScroll: handleOnScroll }), [handleOnScroll]);

  return (
    <Layout
      header={Platform.OS !== "web" ? null : undefined}
      scrollEnabled={Platform.OS !== "web"}
      stickyHeader={Platform.OS !== "web"}
      style={{
        header: (Platform.OS === "web" && [layoutStyle.header, headerAnimatedStyle]) as StyleProp<ViewStyle>,
      }}
    >
      <Animated.View
        style={[StyleSheet.absoluteFill, Platform.OS === "web" && [headerAnimatedStyle, scrollViewAnimatedStyle]]}
        testID="offering-layout"
      >
        <View style={style.header}>{header}</View>

        <View style={style.content}>
          <OfferingLayoutContext.Provider value={value}>{children}</OfferingLayoutContext.Provider>
        </View>
      </Animated.View>

      <PortalHost name={OFFERING_FOOTER_PORTAL_HOST} />
    </Layout>
  );
};

export { OfferingLayout, OFFERING_FOOTER_PORTAL_HOST, useOfferingLayout };
